import React, { useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Popconfirm, Card, App as AntApp } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '@/api/axios';

interface PageTemplate {
    id: string;
    name: string;
    htmlContent: string;
    fieldsSchema: string;
    createdAt: string;
    updatedAt?: string;
}

const PageTemplates: React.FC = () => {
    const { message } = AntApp.useApp();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form] = Form.useForm();

    // Fetch Templates
    const { data: templates, isLoading } = useQuery({
        queryKey: ['page-templates'],
        queryFn: async () => {
            const response = await axios.get('/admin/page-templates');
            return response.data.data as PageTemplate[];
        }
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: async (values: Partial<PageTemplate>) => {
            const response = await axios.post('/admin/page-templates', values);
            return response.data;
        },
        onSuccess: () => {
            message.success('Template created successfully');
            queryClient.invalidateQueries({ queryKey: ['page-templates'] });
            setIsModalVisible(false);
            form.resetFields();
        },
        onError: () => {
            message.error('Failed to create template');
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (data: { id: string; values: Partial<PageTemplate> }) => {
            await axios.put(`/admin/page-templates/${data.id}`, { id: data.id, ...data.values });
        },
        onSuccess: () => {
            message.success('Template updated successfully');
            queryClient.invalidateQueries({ queryKey: ['page-templates'] });
            setIsModalVisible(false);
            setEditingId(null);
            form.resetFields();
        },
        onError: () => {
            message.error('Failed to update template');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await axios.delete(`/admin/page-templates/${id}`);
        },
        onSuccess: () => {
            message.success('Template deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['page-templates'] });
        },
        onError: () => {
            message.error('Failed to delete template');
        }
    });

    // Handlers
    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: PageTemplate) => {
        setEditingId(record.id);
        form.setFieldsValue({
            name: record.name,
            htmlContent: record.htmlContent,
            fieldsSchema: record.fieldsSchema
        });
        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Validate JSON schema
            try {
                JSON.parse(values.fieldsSchema);
            } catch (e) {
                message.error('Fields Schema must be valid JSON');
                return;
            }

            if (editingId) {
                updateMutation.mutate({ id: editingId, values });
            } else {
                createMutation.mutate(values);
            }
        } catch (error) {
            console.error('Validation Error', error);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text: string) => new Date(text).toLocaleString(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: PageTemplate) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this template?"
                        onConfirm={() => deleteMutation.mutate(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small">
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Page Templates</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Add Template
                </Button>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={templates}
                    rowKey="id"
                    loading={isLoading}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Template' : 'Add Template'}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingId(null);
                }}
                width={800}
                destroyOnHidden
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Template Name"
                        rules={[{ required: true, message: 'Please input template name!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="htmlContent"
                        label="HTML Content (use {{Key}} for placeholders)"
                        rules={[{ required: true, message: 'Please input HTML content!' }]}
                    >
                        <Input.TextArea rows={12} style={{ fontFamily: 'monospace' }} />
                    </Form.Item>
                    <Form.Item
                        name="fieldsSchema"
                        label="Fields Schema (JSON Array)"
                        rules={[{ required: true, message: 'Please input Fields Schema!' }]}
                        tooltip="Example: [{&quot;key&quot;:&quot;HeroTitle&quot;,&quot;label&quot;:&quot;Heading&quot;,&quot;type&quot;:&quot;text&quot;}, {&quot;key&quot;:&quot;HeroContent&quot;,&quot;label&quot;:&quot;Text&quot;,&quot;type&quot;:&quot;richtext&quot;}]"
                    >
                        <Input.TextArea rows={8} style={{ fontFamily: 'monospace' }} placeholder="[]" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PageTemplates;
